export type GroupKind = 'select' | 'scale' | 'color';

export interface ClassGroup {
	id: string;
	label: string;
	kind: GroupKind;
	test: RegExp;
	/** valores prontos (select/scale). Cor é livre e usa `prefix`. */
	options?: string[];
	/** para kind 'color': monta bg-[#hex], text-[#hex]… */
	prefix?: string;
}

const SPACING = ['0', '1', '2', '3', '4', '5', '6', '8', '10', '12', '16', '20', '24'];
const COLOR = String.raw`\[#[0-9a-fA-F]{3,8}\]|white|black|transparent|[a-z]+-\d{2,3}`;
const MEDIDA = String.raw`\[\d+(?:\.\d+)?(?:px|rem|em|pt|%)\]`;

export const CLASS_GROUPS: ClassGroup[] = [
	{
		id: 'display',
		label: 'Exibição',
		kind: 'select',
		test: /^(flex|grid|block|inline-flex|inline-block|hidden)$/,
		options: ['flex', 'grid', 'block', 'inline-flex', 'hidden'],
	},
	{
		id: 'direction',
		label: 'Direção',
		kind: 'select',
		test: /^flex-(row|col)(-reverse)?$/,
		options: ['flex-row', 'flex-col'],
	},
	{
		id: 'align',
		label: 'Alinhar',
		kind: 'select',
		test: /^items-(start|center|end|stretch|baseline)$/,
		options: ['items-start', 'items-center', 'items-end', 'items-stretch'],
	},
	{
		id: 'justify',
		label: 'Distribuir',
		kind: 'select',
		test: /^justify-(start|center|end|between|around|evenly)$/,
		options: ['justify-start', 'justify-center', 'justify-end', 'justify-between'],
	},
	{
		id: 'gap',
		label: 'Espaço entre',
		kind: 'scale',
		test: /^gap-(px|\d+(\.5)?|\[[^\]]+\])$/,
		options: SPACING.map((n) => `gap-${n}`),
	},
	{
		id: 'padding',
		label: 'Preenchimento',
		kind: 'scale',
		test: /^p-(px|\d+(\.5)?|\[[^\]]+\])$/,
		options: SPACING.map((n) => `p-${n}`),
	},
	{
		id: 'width',
		label: 'Largura',
		kind: 'select',
		test: /^w-(full|auto|fit|screen|\d+(\/\d+)?|\[[^\]]+\])$/,
		options: ['w-auto', 'w-full', 'w-fit', 'w-1/2', 'w-screen'],
	},
	{
		id: 'radius',
		label: 'Cantos',
		kind: 'select',
		test: /^rounded(-(none|sm|md|lg|xl|2xl|3xl|full|\[[^\]]+\]))?$/,
		options: ['rounded-none', 'rounded-md', 'rounded-xl', 'rounded-2xl', 'rounded-full'],
	},
	{
		id: 'shadow',
		label: 'Sombra',
		kind: 'select',
		test: /^shadow(-(sm|md|lg|xl|2xl|inner|none|\[[^\]]+\]))?$/,
		options: ['shadow-none', 'shadow-sm', 'shadow-md', 'shadow-lg', 'shadow-2xl'],
	},
	{
		id: 'borderWidth',
		label: 'Borda',
		kind: 'select',
		test: new RegExp(String.raw`^border(-([02489]|${MEDIDA}))?$`),
		options: ['border-0', 'border', 'border-2', 'border-4'],
	},
	{ id: 'bg', label: 'Fundo', kind: 'color', prefix: 'bg', test: new RegExp(`^bg-(${COLOR})$`) },
	{ id: 'borderColor', label: 'Cor da borda', kind: 'color', prefix: 'border', test: new RegExp(`^border-(${COLOR})$`) },
	{ id: 'color', label: 'Cor do texto', kind: 'color', prefix: 'text', test: new RegExp(`^text-(${COLOR})$`) },
	{
		id: 'fontSize',
		label: 'Tamanho',
		kind: 'select',
		test: new RegExp(String.raw`^text-(xs|sm|base|lg|xl|[2-9]xl|${MEDIDA})$`),
		options: ['text-xs', 'text-sm', 'text-base', 'text-lg', 'text-xl', 'text-2xl', 'text-3xl'],
	},
	{
		id: 'weight',
		label: 'Peso',
		kind: 'select',
		test: /^font-(thin|light|normal|medium|semibold|bold|extrabold|black)$/,
		options: ['font-normal', 'font-medium', 'font-semibold', 'font-bold'],
	},
	{
		id: 'textAlign',
		label: 'Alinhamento',
		kind: 'select',
		test: /^text-(left|center|right|justify)$/,
		options: ['text-left', 'text-center', 'text-right', 'text-justify'],
	},
];

export function group(id: string): ClassGroup | undefined {
	return CLASS_GROUPS.find((g) => g.id === id);
}

export function tokens(className: string): string[] {
	return className.split(/\s+/).filter(Boolean);
}

/** O token do grupo dentro da classe, se houver. */
export function getClass(className: string, groupId: string): string | null {
	const g = group(groupId);
	if (!g) return null;
	return tokens(className).find((t) => g.test.test(t)) ?? null;
}

/** Troca no lugar (preserva a ordem), remove com null, acrescenta se faltava. */
export function setClass(className: string, groupId: string, token: string | null): string {
	const g = group(groupId);
	if (!g) return className;

	let replaced = false;
	const out: string[] = [];
	for (const t of tokens(className)) {
		if (!g.test.test(t)) {
			out.push(t);
			continue;
		}
		if (replaced) continue; // duplicata do mesmo grupo: some
		replaced = true;
		if (token) out.push(token);
	}
	if (!replaced && token) out.push(token);
	return out.join(' ');
}

export function normalizaCor(valor: string): string | null {
	const bruto = valor.trim();
	if (/^#[0-9a-fA-F]{3,8}$/.test(bruto)) return bruto.toLowerCase();

	const rgbas = [...bruto.matchAll(/rgba?\(([^)]+)\)/g)];
	const partes = rgbas[rgbas.length - 1]?.[1].split(',').map((n) => Number(n.trim()));
	if (!partes || partes.length < 3 || partes.some((n) => !Number.isFinite(n))) return null;

	const canal = (n: number) =>
		Math.max(0, Math.min(255, Math.round(n)))
			.toString(16)
			.padStart(2, '0');
	const alpha = partes[3];
	const opacidade = alpha === undefined || alpha >= 1 ? '' : canal(alpha * 255);
	return `#${canal(partes[0])}${canal(partes[1])}${canal(partes[2])}${opacidade}`;
}

/** #ff0000 → bg-[#ff0000]. Cor arbitrária evita depender da paleta do Tailwind. */
export function colorToken(groupId: string, cor: string): string | null {
	const g = group(groupId);
	const hex = normalizaCor(cor);
	return g?.prefix && hex ? `${g.prefix}-[${hex}]` : null;
}

/** bg-[#ff0000] → #ff0000. Cor nomeada (bg-red-500) devolve null: não é hex. */
export function tokenToColor(token: string | null): string | null {
	return token ? (/-\[(#[0-9a-fA-F]{3,8})\]$/.exec(token)?.[1] ?? null) : null;
}
